import type { CrimeType } from '../core/events';
import { clamp, type V2 } from '../core/math';

/**
 * Pure wanted-level model. No physics, no rendering, no services: the heat system feeds it crimes
 * (with witness counts), police sightings and the player's situation each step, and reads back the
 * level, phase and search area. Everything is deterministic given the same inputs.
 *
 *   points ──(thresholds + hysteresis)──> level 0..4
 *   phase:  none ── crime reported ──> search ── police see the player ──> pursuit
 *                         ^                                                  │
 *                         └──── sight lost for LOST_SIGHT_GRACE seconds ─────┘
 *   During search the points bleed away so that they reach 0 when the search times out;
 *   being seen again freezes the bleed and restarts the clock.
 */

/** Points needed to reach each level (index = level). */
export const LEVEL_THRESHOLDS = [0, 10, 35, 75, 120] as const;
export const HEAT_CAP = 160;
/** A level is lost only once points fall below threshold × this (no flicker at the boundary). */
export const DOWN_HYSTERESIS = 0.85;
/** Seconds of not being seen before police switch from pursuit to search. */
export const LOST_SIGHT_GRACE = 2.5;
/** Seconds of unbroken searching (per level) before the police give up. */
export const SEARCH_DURATION = [0, 12, 20, 28, 38] as const;
/** Search circle: starting radius, growth (m/s) and maximum, per level. */
export const SEARCH_RADIUS_START = [0, 30, 40, 50, 60] as const;
export const SEARCH_RADIUS_MAX = [0, 70, 95, 125, 155] as const;
export const SEARCH_GROWTH = 3.5;
/** Hiding (still, in cover) makes the search run out this much faster. */
export const HIDING_SPEEDUP = 1.8;
/** While police watch a fleeing suspect, points creep up (per second) - long chases escalate. */
export const CHASE_ESCALATION = [0, 0.9, 0.55, 0.35, 0] as const;
/** Chase escalation never pushes past this (level 4 needs real mayhem). */
export const CHASE_ESCALATION_CAP = LEVEL_THRESHOLDS[4] - 5;
/** Below level 1 leftover points fade at this rate. */
export const IDLE_DECAY = 0.6;

export const CRIME_BASE: Record<CrimeType, number> = {
  carTheft: 8,
  carjack: 14,
  policeCarTheft: 40,
  assault: 16,
  pedHit: 14,
  pedKilled: 30,
  vehicleDamage: 4,
  hitPolice: 20,
  recklessDriving: 5,
  trespass: 6,
  robbery: 35,
  destruction: 18,
  // Weapons (offline; docs/WEAPONS.md). Guns are rare in 1970 Kessmouth and treated accordingly:
  // a shot nobody saw still reaches level 2 (gunfire is heard), a shot a copper saw reaches 3.
  firearmDischarge: 40,
  shooting: 30,
  brandishing: 12,
};

/** Crimes that are heard, not only seen: somebody always phones in gunfire (see heatSystem). */
export const HEARD_CRIMES: ReadonlySet<CrimeType> = new Set<CrimeType>(['firearmDischarge', 'shooting']);
/** How far a gunshot carries for the "somebody phones it in" rule (m, through walls). */
export const GUNSHOT_EARSHOT = 90;

export interface Witnesses {
  police: number;
  civilians: number;
}

/** 0 = nobody saw it; civilians report (≤ 1); police seeing it directly count 1.5×. */
export function witnessFactor(w: Witnesses): number {
  if (w.police > 0) return 1.5;
  if (w.civilians > 0) return Math.min(1, 0.5 + 0.17 * w.civilians);
  return 0;
}

export function crimePoints(type: CrimeType, severity: number, w: Witnesses): number {
  const base = CRIME_BASE[type] ?? 5;
  return base * (0.6 + 0.2 * clamp(severity, 1, 5)) * witnessFactor(w);
}

/*
 * Accidents (docs/POLICE.md §1 "Accidents and warnings", owner playtest 2026-10-10: "one accidental bump
 * and the chase is on"). Shared by the offline heat system and the server (server/police/heat.ts), and the
 * thresholds below by the places that emit the crimes (traffic, peds, police; server/police/force.ts).
 */
/** A civilian car shoved by less than this (its Δv, m/s) was bumped, not rammed: no crime at all. */
export const CAR_BUMP_MIN_DV = 2.5;
/** A pedestrian hit below this speed (m/s) is a brush (severity 1); up to PED_HIT_FAST it is severity 2. */
export const PED_BRUSH_SPEED = 4;
export const PED_HIT_FAST = 6;
/** Running into a police car closing at less than this (m/s) is a tap (hitPolice severity 1), not a ram. */
export const POLICE_TAP_SPEED = 5;
/** Minor offences score this share, wanted or not: they are accidents (bumps in a getaway too). */
export const ACCIDENT_MUL = 0.5;
/** After a warning, another minor offence an officer sees within this long (s) is a chase after all. */
export const WARNING_MEMORY = 60;
/** A pedestrian knocked down (not killed) while already wanted scores this share: getaways go wrong. */
export const WANTED_PED_HIT_MUL = 0.45;

/** An accident rather than a crime: a bump, a brush, a tap on a police car, a bit fast past a copper. */
export function isMinorOffence(type: CrimeType, severity: number): boolean {
  if (type === 'vehicleDamage' || type === 'recklessDriving') return true;
  if (type === 'pedHit') return severity <= 2;
  if (type === 'hitPolice') return severity <= 1;
  return false;
}

export interface OffenceScore {
  points: number;
  /** An officer saw a first minor offence while you weren't wanted: a warning, not a chase. */
  warning: boolean;
}

/**
 * Points for a crime given the offender's current wanted `level` and whether they were `warned` recently.
 * A minor offence scores ACCIDENT_MUL of its points, and the first one an officer sees while you're not
 * wanted is a warning (the caller keeps the points under level 1); a pedestrian knocked down mid-chase
 * scores WANTED_PED_HIT_MUL (instead). Everything else is `crimePoints`.
 */
export function scoreOffence(type: CrimeType, severity: number, w: Witnesses, level: number, warned: boolean): OffenceScore {
  let points = crimePoints(type, severity, w);
  if (points <= 0) return { points: 0, warning: false };
  const minor = isMinorOffence(type, severity);
  if (level > 0 && type === 'pedHit') points *= WANTED_PED_HIT_MUL;
  else if (minor) points *= ACCIDENT_MUL;
  return { points, warning: level === 0 && minor && w.police > 0 && !warned };
}

/*
 * "Laid on the tarmac" (docs/POLICE.md §14, research R1): a pursuing unit that crashes out of the chase by
 * its own driving (a wall, traffic, another unit, a spin-out; not a ram of yours, not a shot) buys relief.
 * Shared by the offline heat model and the server (server/police/heat.ts).
 */
/** Search radius (m) a forced crash starts the search at, by level: the starting circle of the level below. */
export const UNIT_DOWN_RADIUS = [0, 20, 30, 40, 50] as const;
/** Below this level a forced crash never takes a level off (the level-1 search is short as it is). */
export const UNIT_DOWN_DROP_FROM = 2;

/** Forced crashes at `level` (counted since you reached it) that take one level off: N at level N (CW's rule). */
export function unitDownsToDrop(level: number): number {
  return level >= UNIT_DOWN_DROP_FROM ? level : Infinity;
}

/** What a forced crash did (for the "UNIT DOWN" stamp and the toast). */
export interface UnitDownResult {
  /** It counted (you were wanted). */
  counted: boolean;
  /** Forced crashes at the (new) level so far, and how many take a level off (Infinity = never). */
  downs: number;
  needed: number;
  /** A level came off. */
  dropped: boolean;
  /** Nobody else had eyes on you: the search began at once. */
  searchNow: boolean;
  level: number;
}

/** Points a warning leaves you on: just under level 1. */
export const WARNING_POINTS_CAP = LEVEL_THRESHOLDS[1] - 0.5;

/** Level for a points value, given the current level (hysteresis on the way down). */
export function levelFor(points: number, current: number): number {
  let up = 0;
  for (let l = 4; l >= 1; l--) {
    if (points >= LEVEL_THRESHOLDS[l]!) {
      up = l;
      break;
    }
  }
  if (up >= current) return up;
  // Dropping: only fall to the highest level whose (lowered) threshold we're still above.
  let level = up;
  for (let l = current; l > up; l--) {
    if (points >= LEVEL_THRESHOLDS[l]! * DOWN_HYSTERESIS) {
      level = l;
      break;
    }
  }
  return level;
}

export type HeatPhase = 'none' | 'search' | 'pursuit';

/** What the police know about the suspect. */
export interface Suspect {
  pos: V2;
  time: number;
  /** Vehicle the suspect was last seen in (null = on foot). */
  vehicleId: number | null;
}

export interface SearchArea {
  center: V2;
  radius: number;
  /** Seconds searched so far. */
  elapsed: number;
  duration: number;
}

export interface HeatSituation {
  /** At least one officer can see (and recognises) the player this step. */
  seen: boolean;
  /** Player is still inside a cover zone (lockup, alley...). */
  hidden: boolean;
  /** Player speed (m/s) - chase escalation only while actually fleeing. */
  playerSpeed: number;
}

export class HeatModel {
  points = 0;
  level = 0;
  phase: HeatPhase = 'none';
  suspect: Suspect | null = null;
  search: SearchArea | null = null;
  /** Seconds since police last saw the player. */
  unseenFor = 0;
  /** Seconds of continuous pursuit (UI / tuning). */
  pursuitTime = 0;
  /** Game time until which the player has had their warning (a minor offence an officer saw). */
  warnedUntil = -Infinity;
  /** The last `addCrime` was a warning instead of a chase. */
  lastWarning = false;
  /** Forced crashes ("laid on the tarmac") counted at `unitsDownLevel`; a level change starts the count again. */
  unitsDown = 0;
  unitsDownLevel = 0;
  private searchBleed = 0;

  /**
   * Score a crime. Returns the points added (0 if nobody saw it). `suspect` is where/how the culprit was
   * seen. A crime seen by police starts a pursuit straight away; one reported by civilians starts a search
   * around the scene. A first minor offence an officer sees while you're not wanted is a warning
   * (`lastWarning`): the points stay under level 1 and nobody gives chase (`scoreOffence`).
   */
  addCrime(type: CrimeType, severity: number, w: Witnesses, suspect: Suspect): number {
    const { points: pts, warning } = scoreOffence(type, severity, w, this.level, suspect.time < this.warnedUntil);
    this.lastWarning = warning;
    if (pts <= 0) return 0;
    if (warning) {
      this.warnedUntil = suspect.time + WARNING_MEMORY;
      const before = this.points;
      this.points = Math.max(before, Math.min(WARNING_POINTS_CAP, before + pts));
      return this.points - before;
    }
    this.points = Math.min(HEAT_CAP, this.points + pts);
    // Police seeing anything at all means at least a level-1 response.
    if (w.police > 0) this.points = Math.max(this.points, LEVEL_THRESHOLDS[1]);
    this.level = Math.max(this.level, levelFor(this.points, this.level));
    if (w.police > 0) {
      this.sighting(suspect);
    } else if (this.level > 0) {
      // Reported: the police know roughly where. During a search that's a tip-off - the circle
      // recentres on the new crime but the clock keeps running (a worse level only lengthens it);
      // otherwise a search starts around the scene.
      this.suspect = { ...suspect, pos: { ...suspect.pos } };
      if (this.phase === 'search' && this.search) {
        this.search.center = { ...suspect.pos };
        this.search.duration = Math.max(this.search.duration, SEARCH_DURATION[this.level]!);
        this.recomputeBleed();
      } else if (this.phase !== 'pursuit') this.beginSearch(suspect.pos);
      else this.recomputeBleed();
    }
    return pts;
  }

  /** Police can see the suspect right now. */
  sighting(s: Suspect): void {
    this.suspect = { pos: { ...s.pos }, time: s.time, vehicleId: s.vehicleId };
    this.unseenFor = 0;
    if (this.level === 0) return;
    this.phase = 'pursuit';
    this.search = null;
  }

  /** Does the current suspect description fit someone in `vehicleId` (null = on foot)? */
  matchesDescription(vehicleId: number | null): boolean {
    if (!this.suspect) return true;
    return this.suspect.vehicleId === vehicleId;
  }

  setLevel(level: number, suspect: Suspect | null): void {
    const l = clamp(Math.round(level), 0, 4);
    if (l === 0) {
      this.clear();
      return;
    }
    this.points = LEVEL_THRESHOLDS[l]! + 1;
    this.level = l;
    if (suspect) this.sighting(suspect);
    else if (this.phase === 'search') this.recomputeBleed();
  }

  /**
   * One level off (a word with the desk sergeant, src/heat/deskSergeant.ts): the points drop to just over
   * the next threshold down and a running search keeps its place, with the lower level's clock if that is
   * shorter. From level 1 it clears. Returns the new level.
   */
  dropOne(): number {
    if (this.level <= 1) {
      this.clear();
      return 0;
    }
    this.level -= 1;
    this.points = Math.min(this.points, LEVEL_THRESHOLDS[this.level]! + 1);
    if (this.search) {
      this.search.duration = Math.min(this.search.duration, SEARCH_DURATION[this.level]!);
      this.search.radius = Math.min(this.search.radius, SEARCH_RADIUS_MAX[this.level]!);
    }
    if (this.phase === 'search') this.recomputeBleed();
    return this.level;
  }

  /**
   * A pursuing unit crashed out by its own driving ("laid on the tarmac"; the police module decides what
   * counts). At level N the N-th such crash takes a level off (`unitDownsToDrop`); and when no other officer
   * can see you (`seenByOthers`) the pursuit turns into a search at once, at the smaller circle of the level
   * below (`UNIT_DOWN_RADIUS`), instead of after LOST_SIGHT_GRACE.
   */
  unitDown(seenByOthers: boolean): UnitDownResult {
    if (this.level === 0) return { counted: false, downs: 0, needed: Infinity, dropped: false, searchNow: false, level: 0 };
    if (this.unitsDownLevel !== this.level) {
      this.unitsDown = 0;
      this.unitsDownLevel = this.level;
    }
    this.unitsDown++;
    const at = this.level;
    let dropped = false;
    if (this.unitsDown >= unitDownsToDrop(this.level)) {
      this.dropOne();
      dropped = true;
      this.unitsDown = 0;
      this.unitsDownLevel = this.level;
    }
    let searchNow = false;
    if (!seenByOthers && this.phase === 'pursuit') {
      this.beginSearch(this.suspect ? this.suspect.pos : { x: 0, z: 0 });
      if (this.search) this.search.radius = UNIT_DOWN_RADIUS[at]!;
      searchNow = true;
    }
    return { counted: true, downs: this.unitsDown, needed: unitDownsToDrop(this.level), dropped, searchNow, level: this.level };
  }

  clear(): void {
    this.unitsDown = 0;
    this.unitsDownLevel = 0;
    this.points = 0;
    this.warnedUntil = -Infinity;
    this.lastWarning = false;
    this.level = 0;
    this.phase = 'none';
    this.search = null;
    this.suspect = null;
    this.unseenFor = 0;
    this.pursuitTime = 0;
    this.searchBleed = 0;
  }

  /** Advance one step. Returns true if the police gave up this step (heat fully cleared). */
  update(dt: number, s: HeatSituation): boolean {
    if (this.level === 0) {
      this.points = Math.max(0, this.points - IDLE_DECAY * dt);
      this.phase = 'none';
      this.search = null;
      return false;
    }
    if (s.seen) {
      this.unseenFor = 0;
      this.pursuitTime += dt;
      if (this.phase !== 'pursuit') {
        this.phase = 'pursuit';
        this.search = null;
      }
      if (s.playerSpeed > 6 && this.points < CHASE_ESCALATION_CAP) {
        this.points = Math.min(CHASE_ESCALATION_CAP, this.points + CHASE_ESCALATION[this.level]! * dt);
        this.level = Math.max(this.level, levelFor(this.points, this.level));
      }
      return false;
    }
    this.unseenFor += dt;
    if (this.phase === 'pursuit' && this.unseenFor >= LOST_SIGHT_GRACE) {
      this.beginSearch(this.suspect ? this.suspect.pos : { x: 0, z: 0 });
    }
    if (this.phase === 'search' && this.search) {
      const k = s.hidden ? HIDING_SPEEDUP : 1;
      const area = this.search;
      area.elapsed += dt * k;
      area.radius = Math.min(SEARCH_RADIUS_MAX[this.level]!, area.radius + SEARCH_GROWTH * dt);
      this.points = Math.max(0, this.points - this.searchBleed * dt * k);
      // Stars fade one by one while they look, but the last one stays until they actually give up.
      this.level = Math.max(1, levelFor(this.points, this.level));
      if (area.elapsed >= area.duration) {
        this.clear();
        return true;
      }
    }
    return false;
  }

  /**
   * A civilian phoned in a sighting during a search: the search recentres on the new position (the
   * clock keeps running - it is a tip-off, not a sighting by an officer).
   */
  tipOff(pos: V2, now: number, vehicleId: number | null): boolean {
    if (this.phase !== 'search' || !this.search) return false;
    this.search.center = { x: pos.x, z: pos.z };
    this.suspect = { pos: { x: pos.x, z: pos.z }, time: now, vehicleId };
    return true;
  }

  /** 0..1 of the way through the current search (for UI). */
  get searchProgress(): number {
    return this.search ? clamp(this.search.elapsed / this.search.duration, 0, 1) : 0;
  }

  private beginSearch(center: V2): void {
    this.phase = 'search';
    this.pursuitTime = 0;
    const l = Math.max(1, this.level);
    this.search = { center: { ...center }, radius: SEARCH_RADIUS_START[l]!, elapsed: 0, duration: SEARCH_DURATION[l]! };
    this.recomputeBleed();
  }

  /** Bleed rate so the points reach zero exactly when the search would time out. */
  private recomputeBleed(): void {
    const l = Math.max(1, this.level);
    const left = this.search ? Math.max(1, this.search.duration - this.search.elapsed) : SEARCH_DURATION[l]!;
    this.searchBleed = this.points / left;
  }
}
